import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Lock, Calendar, Users, Bed } from 'lucide-react';
import { useBooking } from '../hooks/useBooking';
interface PaymentProps {
  onConfirm: () => void;
  onBack: () => void;
}
export function Payment({
  onConfirm,
  onBack
}: PaymentProps) {
  const {
    selectedHotel,
    selectedRoom,
    checkIn,
    checkOut,
    adults,
    rooms
  } = useBooking();
  if (!selectedHotel || !selectedRoom) {
    return <div className="pt-32 pb-20 text-center min-h-screen bg-luxury-cream" data-id="element-284">
        <p data-id="element-285">Missing booking details.</p>
        <button onClick={onBack} className="mt-4 text-luxury-blue underline" data-id="element-286">
          Go back
        </button>
      </div>;
  }
  // Calculate nights and prices
  const nights = checkIn && checkOut ? Math.max(1, Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))) : 1;
  const pricePerNight = selectedHotel.price + selectedRoom.priceModifier;
  const roomTotal = pricePerNight * nights * rooms;
  const taxes = Math.round(roomTotal * 0.12);
  const finalTotal = roomTotal + taxes;
  const formatDate = (date: Date | null) => {
    if (!date) return 'Not set';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm();
  };
  return <div className="pt-24 pb-20 min-h-screen bg-luxury-cream" data-id="element-287">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-288">
        <button onClick={onBack} className="flex items-center text-gray-500 hover:text-luxury-blue transition-colors mb-8 font-medium text-sm" data-id="element-289">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-290" />
          Back to room selection
        </button>

        <h1 className="text-3xl md:text-4xl font-italiana text-luxury-slate mb-8" data-id="element-291">
          Complete Your Booking
        </h1>

        <div className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-292">
          {/* Left Column - Form */}
          <div className="w-full lg:w-3/5 space-y-8" data-id="element-293">
            <form onSubmit={handleSubmit} className="space-y-8" data-id="element-294">
              {/* Guest Details */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100" data-id="element-295">
                <h2 className="text-xl font-semibold text-luxury-slate mb-6 font-poppins" data-id="element-296">
                  Guest Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-297">
                  <div data-id="element-298">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-299">
                      First Name
                    </label>
                    <input required type="text" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all" placeholder="John" data-id="element-300" />
                  </div>
                  <div data-id="element-301">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-302">
                      Last Name
                    </label>
                    <input required type="text" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all" placeholder="Doe" data-id="element-303" />
                  </div>
                  <div data-id="element-304">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-305">
                      Email Address
                    </label>
                    <input required type="email" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all" placeholder="john@example.com" data-id="element-306" />
                  </div>
                  <div data-id="element-307">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-308">
                      Phone Number
                    </label>
                    <input required type="tel" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all" placeholder="+1 (555) 000-0000" data-id="element-309" />
                  </div>
                </div>
              </motion.div>

              {/* Payment Details */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100" data-id="element-310">
                <div className="flex justify-between items-center mb-6" data-id="element-311">
                  <h2 className="text-xl font-semibold text-luxury-slate font-poppins" data-id="element-312">
                    Payment Details
                  </h2>
                  <div className="flex space-x-2" data-id="element-313">
                    <div className="w-8 h-5 bg-gray-100 rounded flex items-center justify-center" data-id="element-314">
                      <CreditCard className="w-4 h-4 text-gray-400" data-id="element-315" />
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-316">
                  <div data-id="element-317">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-318">
                      Card Number
                    </label>
                    <div className="relative" data-id="element-319">
                      <input required type="text" maxLength={19} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all font-mono" placeholder="0000 0000 0000 0000" data-id="element-320" />
                      <CreditCard className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" data-id="element-321" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6" data-id="element-322">
                    <div data-id="element-323">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-324">
                        Expiry Date
                      </label>
                      <input required type="text" maxLength={5} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all font-mono" placeholder="MM/YY" data-id="element-325" />
                    </div>
                    <div data-id="element-326">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-327">
                        CVV
                      </label>
                      <input required type="text" maxLength={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all font-mono" placeholder="123" data-id="element-328" />
                    </div>
                  </div>
                  <div data-id="element-329">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-330">
                      Name on Card
                    </label>
                    <input required type="text" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-luxury-blue/20 focus:border-luxury-blue outline-none transition-all" placeholder="JOHN DOE" data-id="element-331" />
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-332">
                  <button type="submit" className="w-full bg-luxury-blue hover:bg-blue-600 text-white py-4 rounded-xl font-semibold text-lg transition-colors shadow-md flex items-center justify-center" data-id="element-333">
                    <Lock className="w-5 h-5 mr-2" data-id="element-334" />
                    Confirm Booking • £{finalTotal}
                  </button>
                  <p className="text-center text-xs text-gray-500 mt-4" data-id="element-335">
                    By confirming this booking, you agree to our Terms of
                    Service and Privacy Policy.
                  </p>
                </div>
              </motion.div>
            </form>
          </div>

          {/* Right Column - Summary */}
          <div className="w-full lg:w-2/5" data-id="element-336">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="bg-white rounded-2xl shadow-float border border-gray-100 overflow-hidden sticky top-28" data-id="element-337">
              {/* Summary Header */}
              <div className="p-6 border-b border-gray-100 flex gap-4" data-id="element-338">
                <img src={selectedHotel.image} alt={selectedHotel.name} className="w-24 h-24 rounded-xl object-cover shrink-0" data-id="element-339" />
                <div data-id="element-340">
                  <h3 className="font-italiana text-xl font-bold text-luxury-slate leading-tight mb-1" data-id="element-341">
                    {selectedHotel.name}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-342">
                    {selectedHotel.location}
                  </p>
                  <div className="text-sm font-medium text-luxury-blue mt-2" data-id="element-343">
                    {selectedRoom.name}
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="p-6 border-b border-gray-100 space-y-4" data-id="element-344">
                <div className="flex items-start gap-3" data-id="element-345">
                  <Calendar className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" data-id="element-346" />
                  <div data-id="element-347">
                    <div className="text-sm font-medium text-gray-900" data-id="element-348">
                      {formatDate(checkIn)} — {formatDate(checkOut)}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-349">
                      {nights} night{nights > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-350">
                  <Users className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" data-id="element-351" />
                  <div className="text-sm font-medium text-gray-900" data-id="element-352">
                    {adults} Adult{adults > 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-353">
                  <Bed className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" data-id="element-354" />
                  <div className="text-sm font-medium text-gray-900" data-id="element-355">
                    {rooms} Room{rooms > 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="p-6 bg-gray-50" data-id="element-356">
                <h4 className="font-semibold text-gray-900 mb-4" data-id="element-357">
                  Price Breakdown
                </h4>
                <div className="space-y-3 text-sm" data-id="element-358">
                  <div className="flex justify-between text-gray-600" data-id="element-359">
                    <span data-id="element-360">
                      £{pricePerNight} × {nights} night{nights > 1 ? 's' : ''}{' '}
                      {rooms > 1 ? `× ${rooms} rooms` : ''}
                    </span>
                    <span data-id="element-361">£{roomTotal}</span>
                  </div>
                  <div className="flex justify-between text-gray-600" data-id="element-362">
                    <span data-id="element-363">Taxes & Fees</span>
                    <span data-id="element-364">£{taxes}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-200 flex justify-between items-center" data-id="element-365">
                    <span className="font-semibold text-gray-900 text-base" data-id="element-366">
                      Total
                    </span>
                    <span className="font-bold text-xl text-luxury-slate" data-id="element-367">
                      £{finalTotal}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}